// El contexto de la cuenta del shell, en un módulo HOJA: sin esto, cualquier pantalla
// que quiera `useCuentaShell` importa `EstiloMinimal` entero (que monta el chat), y una
// pantalla que el chat también abre (Subir productos, los consejos, desde el selector de
// fuentes) cerraba un import circular con `chat-atlas` — mismo arreglo que
// `chat-atlas/entornoContext.ts`. `EstiloMinimal` lo provee y lo reexporta.

import { createContext, useContext } from "react";
import type { MotivoPricing } from "../../components/pricing/pricingContexts";
import type { FilaDeHerramienta } from "./SeccionesDeLaSidebar";

/**
 * Abre el catálogo como overlay. Lo provee el shell para que lo consuma cualquier
 * descendiente, incluidas las secciones montadas en el `<Outlet />`: el estado del
 * overlay tiene que ser del shell, porque si viviera en la sección cambiar de
 * pantalla lo cerraría.
 *
 * Lleva además el **estado de cuenta** (plan y créditos) porque lo consume la misma
 * gente que la acción: el encabezado de la home muestra los créditos al lado del CTA
 * de mejora (ADR-2026-07-31-creditos-mejora-plan-encabezado-home), y esos números viven
 * en el shell. Pasarlos por props obligaría a que cada sección hospedada los reciba y
 * los reenvíe.
 *
 * Los defaults son inertes para que un consumidor montado fuera del shell no explote.
 */
export interface CuentaCtx {
  abrirPricing: (motivo?: MotivoPricing) => void;
  creditos: number;
  planLabel: string;
  /**
   * `true` cuando no hay nada que resolver: plan pago **y** saldo por encima del
   * umbral. Es el criterio que decide si el CTA de mejora va sólido o en tinte
   * (ADR-2026-07-31-columna-contenido-90-mejorar-plan); se calcula una vez en el shell para que los dos botones —el de la
   * sidebar y el del encabezado— no puedan discrepar.
   */
  planAlDia: boolean;
  /* Identidad real del usuario — ADR-2026-07-31-shell-nuevo-datos-reales. Viaja por el contexto
     y no por props por lo mismo que el plan: la consumen los cuatro avatares, la
     cabecera del menú de cuenta y el saludo de la home, repartidos entre el shell
     y la sección. */
  /** `profiles.name`, o `null` mientras carga / si la fila no lo trae. */
  nombreCompleto: string | null;
  /** `profiles.email`, con el de la sesión como respaldo. */
  email: string | null;
  /** Inicial del avatar, ya derivada y en mayúscula (ver `inicialDe`). */
  inicial: string;
  /** Empresa del usuario — la home la necesita para sus creaciones recientes. */
  idCompany: string | null;
  /**
   * Marca activa (`brand_profiles.id`) — ADR-2026-08-03-creaciones-recientes-por-marca.
   * La consumen las secciones que muestran contenido generado, que es contenido DE
   * una marca y no de la cuenta. `null` mientras resuelve.
   */
  idMarca: string | null;
  /**
   * Rubro de la marca activa (`brand_profiles.rubro`) —
   * ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
   *
   * Va en el contexto y no como prop porque quien lo necesita es la TIRA DE MODOS, tres
   * niveles abajo, y sólo para elegir una carátula: enhebrarlo por props obligaba a
   * `HomeMinimal` —que no decide nada del rubro— a recibirlo y pasarlo. El shell ya lo
   * tiene resuelto para filtrar la sidebar, así que es el mismo valor y no una segunda
   * fuente. `null` mientras carga.
   */
  rubroMarca: string | null;
  /**
   * `true` sólo cuando el valor lo provee ESTE shell — ADR-2026-07-31-shell-provee-flag-hospedaje.
   *
   * Lo consultan las pantallas hospedadas que traen su propio chrome duplicado: la
   * barra de créditos y el panel CONTEXTO DEV los pinta el shell, así que
   * `InterfazV2` tiene que callarse los suyos o quedan dos.
   *
   * Es un flag de contexto y no una comparación de `pathname` ni un `useIsDesktop()`
   * a propósito: la pregunta real es "¿quién me montó?", y las dos aproximaciones se
   * desincronizan del router en cuanto se mueve una ruta. Justamente eso pasó con la
   * versión anterior (`pathname.startsWith('/modo/')`), que quedó siempre `false` al
   * pasar el shell a las URLs viejas (ADR-2026-07-31-shell-nuevo-urls-viejas).
   */
  dentroDelShell: boolean;
  /**
   * Corre `accion` con el MISMO fade de salida que usa la sidebar para cambiar de
   * sección (ADR-2026-08-03-tira-modos-usa-fade-del-shell): apaga el contenido,
   * espera `FADE_SALIDA_MS` y recién ahí ejecuta.
   *
   * Existe porque la tira de modos de la home no navega por su cuenta: el destino lo
   * arma `InterfazV2` (gating de plan → visor de ejemplos, guardado del scroll), así
   * que el shell no puede interceptar una ruta. Lo que se comparte es la TRANSICIÓN,
   * no el destino.
   *
   * Fuera del shell es identidad: se ejecuta en el acto, sin fade.
   */
  conFadeDeSalida: (accion: () => void) => void;
  /**
   * `true` mientras `useCuentaAtlas` no resolvió — ADR-2026-08-03-home-aparece-entera-o-skeleton.
   *
   * Todo lo demás de este contexto ya tiene un valor mientras carga (0 créditos,
   * nombre `null`), que es lo correcto para la sidebar —muestra el hueco y se
   * completa— pero no para la home, que necesita saber si esperar.
   */
  cargandoCuenta: boolean;
  /** Dueño de la empresa (`company_profiles.owner_user_id`). Gatea "Suscripción" en el menú
   *  (spec 2026-09-11-invitaciones-equipo §4.3). */
  esDueno: boolean;
  /**
   * La interfaz nueva —la home por destino y la sidebar por secciones— la ve sólo el
   * grupo C (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva). Se calcula UNA vez acá, del
   * mismo `grupo_testing` que trae `useCuentaAtlas`, para que la home, la sidebar y las
   * pantallas de plantillas no puedan discrepar. Desconocido → `false` (la UI de siempre).
   */
  uiNueva: boolean;
  /**
   * Las herramientas de la interfaz nueva —ediciones rápidas y modos de Herramientas, ya
   * filtradas por rubro y con su candado—, la MISMA lista que el panel "Herramientas" de
   * la sidebar. La dibuja también "Ediciones rápidas" de la home, para que las dos ofrezcan
   * lo mismo. Vacía fuera del shell.
   */
  herramientas: readonly FilaDeHerramienta[];
}

export const CuentaContext = createContext<CuentaCtx>({
  abrirPricing: () => {},
  creditos: 0,
  planLabel: '',
  planAlDia: false,
  nombreCompleto: null,
  email: null,
  inicial: '?',
  idCompany: null,
  idMarca: null,
  rubroMarca: null,
  // Default `false`: un consumidor montado fuera del shell —InterfazV2 en mobile,
  // bajo `DashboardLayout`— tiene que seguir pintando su propio chrome.
  dentroDelShell: false,
  // Sin shell no hay nada que apagar: la acción corre tal cual.
  conFadeDeSalida: (accion) => accion(),
  // Sin shell no hay cuenta que esperar: el consumidor dibuja con lo que tenga.
  cargandoCuenta: false,
  esDueno: false,
  // Sin shell (o sin cuenta todavía) no hay grupo: la interfaz de siempre.
  uiNueva: false,
  herramientas: [],
});

/**
 * Estado de cuenta y hospedaje, para las pantallas montadas en el `<Outlet />`.
 * Se exporta el hook y no el contexto: los consumidores no tienen por qué saber que
 * esto es un `createContext`, y así el default inerte no se puede saltear.
 */
export function useCuentaShell(): CuentaCtx {
  return useContext(CuentaContext);
}
